import { Button } from "@fefyi/ui/button";
import { ArrowRight, BadgeCheck, Loader2 } from "lucide-react";
import confetti from "canvas-confetti";
import { useRef, useState } from "react";
import { getModuleAndLessonIndexFromSlug } from "~/utils/lessons";
// import { useSound } from "@fefyi/ui/utils/use-sound";
import { twMerge } from "tailwind-merge";

type Props = {
  lesson: string;
  course: "motion";
  isCompleted?: boolean;
  nextLessonSlug?: string;
  nextLessonTitle?: string;
  nextLessonUnderDevelopment?: boolean;
  isProMember?: boolean;
  fakeButton?: boolean;
};

export default ({
  course,
  lesson,
  isCompleted,
  nextLessonSlug,
  nextLessonTitle,
  nextLessonUnderDevelopment,
  isProMember,
  fakeButton,
}: Props) => {
  const [isSaving, setIsSaving] = useState(false);
  const [isRedirecting, setIsRedirecting] = useState(false);
  const ref = useRef<SVGSVGElement>(null);
  // const [play] = useSound("/sfx/twinkle.mp3");
  // This boolean is set if a lesson is marked as completed,
  // but no redirect is happening because the next lesson is
  // still under development.
  const [completedWithoutRedirect, setCompletedWithoutRedirect] =
    useState(false);
  const _isCompleted = isCompleted || completedWithoutRedirect;

  const markAsCompleted = async () => {
    setIsSaving(true);
    let res;

    if (!fakeButton) {
      try {
        res = await fetch(`/course/api/mark-as-completed`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ course, lesson }),
        });
      } catch (err) {
        setIsSaving(false);
        return;
      }
    }

    // artifical delay
    await new Promise((resolve) => setTimeout(resolve, 1000));

    if (fakeButton) {
      // play();
      const position = ref.current?.getBoundingClientRect();
      setTimeout(() => {
        confetti({
          spread: 360,
          startVelocity: 25,
          gravity: 0.5,
          origin: {
            x: position?.x ? position.x / window.innerWidth : 0.5,
            y: position?.y ? position.y / window.innerHeight : 0.5,
          },
        });
      }, 200);

      setIsRedirecting(true);
      await new Promise((resolve) => setTimeout(resolve, 2000));

      setIsRedirecting(false);
      setIsSaving(false);
      return;
    }

    if (res?.ok) {
      // play();
      const position = ref.current?.getBoundingClientRect();
      setTimeout(() => {
        confetti({
          spread: 360,
          startVelocity: 25,
          gravity: 0.5,
          origin: {
            x: position?.x ? position.x / window.innerWidth : 0.5,
            y: position?.y ? position.y / window.innerHeight : 0.5,
          },
        });
      }, 200);

      setIsRedirecting(true);
      await new Promise((resolve) => setTimeout(resolve, 2000));

      if (nextLessonSlug && !nextLessonUnderDevelopment) {
        window.location.href = `/course/${nextLessonSlug}`;
      }

      setIsRedirecting(false);
      setIsSaving(false);

      if (nextLessonUnderDevelopment) {
        setCompletedWithoutRedirect(true);
      }
    }
  };

  if (_isCompleted || !isProMember) {
    // @TODO: Catch the possibility if we didn't find a lesson?
    // @TODO: Perhaps even something custom when you're at the end.
    if (!nextLessonSlug) return null;
    const { module, lesson } = getModuleAndLessonIndexFromSlug(nextLessonSlug);
    return (
      <div className="mt-4 flex flex-col gap-3 md:flex-row md:items-center md:gap-5">
        {nextLessonUnderDevelopment ? (
          <p className="text-2xl md:text-4xl">
            Next lesson
            <span className="ml-5 block text-lg md:ml-0 md:text-2xl">
              releasing soon
            </span>
          </p>
        ) : (
          <p className="ml-5 text-2xl md:ml-0 md:text-4xl">Next up</p>
        )}
        <Button
          className={twMerge(
            "group hover:bg-background-contrast focus:bg-background-contrast relative flex max-w-[400px] cursor-pointer gap-5 rounded-3xl bg-transparent p-5 text-left md:items-center md:px-8 md:py-5",
            nextLessonUnderDevelopment && "pointer-events-none opacity-50",
          )}
          href={`/course/${nextLessonSlug}`}
        >
          <p className="flex flex-col text-2xl">
            <span className="text-md">
              Module {module} – Lesson {lesson}
            </span>
            <span>{nextLessonTitle}</span>
          </p>
          <ArrowRight className="shrink-0 transition-transform duration-300 group-hover:translate-x-2 group-focus-visible:translate-x-2" />
        </Button>
      </div>
    );
  }

  return (
    <div className="mt-4 flex flex-col items-center gap-3">
      <Button
        intent="white"
        onClick={markAsCompleted}
        disabled={isSaving}
        data-state={
          isRedirecting ? "redirecting" : isSaving ? "saving" : "idle"
        }
        className="group flex cursor-pointer items-center gap-2 px-8 py-4 text-lg"
      >
        <span className="relative" aria-hidden>
          <BadgeCheck
            className="group-hover:animate-beat group-data-[state=saving]:animate-beat relative block h-6 w-6 transition-opacity duration-500 ease-out group-data-[state=redirecting]:opacity-0"
            ref={ref}
          />
          <span className="absolute inset-0 opacity-0 transition-opacity duration-500 ease-out group-data-[state=redirecting]:opacity-100">
            <Loader2 className="block h-6 w-6 animate-spin" />
          </span>
        </span>

        <span className="relative overflow-hidden">
          <span className="relative block transition-transform duration-500 ease-out group-data-[state=redirecting]:-translate-y-full group-data-[state=saving]:-translate-y-full">
            Mark as completed
          </span>
          <span
            aria-hidden
            className="absolute bottom-0 left-0 block translate-y-full transition-transform duration-500 ease-out group-data-[state=redirecting]:-translate-y-full group-data-[state=saving]:translate-y-0"
          >
            Saving progress..
          </span>
          <span
            aria-hidden
            className="absolute bottom-0 left-0 block translate-y-full transition-transform duration-500 ease-out group-data-[state=redirecting]:translate-y-0"
          >
            Knowledge++;
          </span>
        </span>
      </Button>
      <span className="text-md">
        And{" "}
        <a
          href={`/course/${nextLessonSlug}`}
          className="underline underline-offset-4"
        >
          continue
        </a>{" "}
        to the next lesson
      </span>
    </div>
  );
};
// @TODO: Make aria hidden text accessible.
